import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Loader2, MessageSquareText, Bot, User as UserIcon } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { log } from "@/lib/logger";

const MODELS = [
  { key: "claude-sonnet-5", label: "Claude Sonnet 5", badge: "★ Le plus fin" },
  { key: "claude-sonnet-4-6", label: "Claude Sonnet 4.6", badge: "" },
  { key: "claude-haiku-4-5", label: "Claude Haiku 4.5", badge: "⚡ Rapide" },
  { key: "gemini-flash", label: "Gemini 3 Flash", badge: "" },
];

export default function CoachChat({ open, onOpenChange, targetCareer = null, triggerLabel = null }) {
  const { lang } = useAuth();
  const [messages, setMessages] = useState([]); // {role, content}
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [model, setModel] = useState("claude-sonnet-5");
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    if (!open) return;
    if (messages.length === 0 && targetCareer) {
      const suggest = {
        fr: `Je m'intéresse au métier "${targetCareer}". Par où commencer selon mon pays ?`,
        en: `I'm interested in "${targetCareer}". Where do I start given my country?`,
        es: `Me interesa "${targetCareer}". ¿Por dónde empiezo en mi país?`,
        de: `Ich interessiere mich für "${targetCareer}". Wo fange ich in meinem Land an?`,
      }[lang] || `Tell me how to start a career in ${targetCareer}`;
      setInput(suggest);
    }
  }, [open, targetCareer, lang, messages.length]);

  const uid = () => (globalThis.crypto?.randomUUID?.() ?? `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  const send = async () => {
    const msg = input.trim();
    if (!msg || streaming) return;
    setStreaming(true);
    setMessages((m) => [...m, { id: uid(), role: "user", content: msg }, { id: uid(), role: "assistant", content: "" }]);
    setInput("");
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Bearer JWT — see lib/api.js. fetch() is used directly here to consume
      // the SSE stream (axios can't stream), so we read the token manually.
      const token = localStorage.getItem("proxijob_token");
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/coach/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ session_id: sessionId, message: msg, model, target_career: targetCareer, language: lang }),
        signal: controller.signal,
      });
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += dec.decode(value, { stream: true });
        const lines = buffer.split(/\n\n/);
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5).trim());
            if (payload.delta) {
              setMessages((m) => {
                const last = m[m.length - 1];
                if (last?.role !== "assistant") return m;
                return [...m.slice(0, -1), { ...last, content: last.content + payload.delta }];
              });
            }
            if (payload.session_id) setSessionId(payload.session_id);
            if (payload.error) throw new Error(payload.error);
          } catch (err) {
            // Malformed SSE frame — the stream is still healthy, skip this line.
            log.warn("[coach] bad SSE line dropped:", err?.message || err);
          }
        }
      }
    } catch (e) {
      if (e.name !== "AbortError") {
        setMessages((m) => [...m.slice(0, -1), { id: uid(), role: "assistant", content: `⚠️ ${e.message || "Erreur"}` }]);
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const stop = () => { abortRef.current?.abort(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coach-dialog" className="max-w-2xl h-[80vh] flex flex-col rounded-3xl p-0 overflow-hidden">
        <DialogHeader className="p-5 border-b border-slate-200 shrink-0 bg-gradient-to-r from-[#1E3A8A] to-[#F97316]">
          <DialogTitle className="text-white flex items-center gap-2">
            <Bot className="w-5 h-5" />
            {lang === "fr" ? "Coach Carrière IA" : lang === "es" ? "Coach de carrera IA" : lang === "de" ? "KI-Karrierecoach" : "AI Career Coach"}
            {targetCareer && <span className="text-xs font-normal opacity-80">· {targetCareer}</span>}
          </DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            <select
              data-testid="coach-model-select"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="text-xs bg-white/20 text-white border border-white/40 rounded-full px-3 py-1"
            >
              {MODELS.map((m) => <option key={m.key} value={m.key} className="text-slate-900">{m.label} {m.badge && `— ${m.badge}`}</option>)}
            </select>
            <span className="text-[11px] text-white/70">
              {lang === "fr" ? "Streaming temps réel" : "Live streaming"}
            </span>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-slate-50">
          {messages.length === 0 && (
            <div className="text-center text-slate-400 mt-16">
              <MessageSquareText className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{lang === "fr" ? "Pose ta question — orientation, écoles, entreprises à contacter, doutes…" : "Ask anything — schools, businesses to reach out to, doubts…"}</p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={m.id ?? `msg-${i}`} data-testid={`coach-msg-${i}`} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`}>
              {m.role === "assistant" && (
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#1E3A8A] to-[#F97316] flex items-center justify-center shrink-0 text-white">
                  <Bot className="w-4 h-4" />
                </div>
              )}
              <div className={`rounded-2xl px-4 py-2.5 max-w-[80%] whitespace-pre-wrap leading-relaxed ${
                m.role === "user" ? "bg-[#1E3A8A] text-white" : "bg-white border border-slate-200 text-slate-800"
              }`}>
                {m.content || (m.role === "assistant" && streaming && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />)}
              </div>
              {m.role === "user" && (
                <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0 text-slate-600">
                  <UserIcon className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        <div className="p-4 border-t border-slate-200 bg-white shrink-0">
          <div className="flex gap-2 items-end">
            <Textarea
              data-testid="coach-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={lang === "fr" ? "Écris ta question… (Entrée pour envoyer)" : "Type your question… (Enter to send)"}
              rows={2}
              disabled={streaming}
              className="flex-1 resize-none rounded-2xl border-slate-300"
            />
            {streaming ? (
              <Button data-testid="coach-stop-btn" onClick={stop} variant="outline" className="rounded-full h-11 px-4">
                <Loader2 className="w-4 h-4 animate-spin" />
              </Button>
            ) : (
              <Button data-testid="coach-send-btn" onClick={send} disabled={!input.trim()} className="rounded-full h-11 px-4 bg-[#F97316] hover:bg-[#EA580C]">
                <Send className="w-4 h-4" />
              </Button>
            )}
          </div>
          <p className="text-[10px] text-slate-400 mt-1.5">
            {lang === "fr" ? "Réponses générées par IA (Claude/Gemini via Emergent) — vérifiez toujours les faits sensibles." : "AI-generated (Claude/Gemini via Emergent) — verify sensitive facts."}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
